/**
 * SiteMapLayers — renders SiteLayer overlays on a parent <Map> and (optionally)
 * a floating legend with per-category visibility toggles.
 *
 * The component is split into three pieces:
 *   • SiteLayerOverlays — imperative google.maps.Polygon / Polyline / Marker children
 *     of a parent <Map>. Returns null. Re-renders overlays whenever the layer
 *     list or hidden-categories set changes; cleans up on unmount.
 *   • SiteLayerLegend  — pure UI; controlled component, owns no state.
 *   • SiteMapLayers    — composes the above and owns the hidden-category state.
 *     The legend is opt-out (e.g., the SiteLandingPage preview) via showLegend=false.
 *
 * Visibility precedence: a layer is shown iff `layer.visible !== false` AND its
 * category isn't in `hiddenCategories`. Toggles are viewer-only — no persistence.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { Eye, EyeOff, RotateCcw } from 'lucide-react';

import { Button } from '../ui/button';
import {
  LAYER_COLORS,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  colorForCategory,
} from '../../config/siteLayerColors';
import type { LayerColorTone } from '../../config/siteLayerColors';
import type { SiteLayer, SiteLayerCategory } from '../../types/site';
import type {
  ResolvedBoundaryStudyPolygon,
  SnapGuide,
  SquareCornersPreview,
} from '../../lib/boundaryAlignmentTools';
import { LayerTagChip } from './report/LayerTagChip';
import { layerCategoryKey } from './generationImpact/studio/studioLayers';

/* ── Overlays (imperative, returns null) ──────────────────────────────── */

export interface SiteLayerOverlaysProps {
  layers: SiteLayer[];
  /** Hidden category keys — enum values OR user-defined labels. */
  hiddenCategories?: ReadonlySet<string>;
  /**
   * Per-layer visibility hide-list (Reader Polish Prompt 4). When a
   * layer's id is in this set, it's filtered out regardless of its
   * `visible` field or category state. Used by SiteMapLayersPanel
   * to drive per-row checkboxes without mutating the layer objects.
   */
  hiddenLayerIds?: ReadonlySet<string>;
  /** Geometry kinds hidden by the viewer (SiteMapLayersPanel type chips).
   *  A layer whose `geometry.type` is in this set is filtered out on top of
   *  the category / per-layer hide-lists. */
  hiddenGeometryTypes?: ReadonlySet<SiteLayer['geometry']['type']>;
  /** Polygon fill alpha. Default 0.2. The wizard drawer uses a lower value
   *  so the user's drawn boundary stays visually dominant. */
  fillOpacity?: number;
  strokeOpacity?: number;
  strokeWeight?: number;
  /** P4 — per-category fill-opacity override (0.05–1); falls back to
   *  `fillOpacity` for categories without an entry. */
  categoryOpacity?: Readonly<Record<string, number>>;
  /**
   * OPTIONAL selection (Test-Fit Studio). When supplied, clicking a layer
   * selects it — reporting whether a modifier key was held so the caller can
   * extend a multi-selection — and ids in `selectedLayerIds` render with an
   * emphasised stroke. Consumers that omit these (the Site Map) keep the
   * original info-window-only behaviour unchanged.
   */
  onLayerClick?: (layerId: string, additive: boolean) => void;
  selectedLayerIds?: ReadonlySet<string>;
}

/** Selected overlays read as selected without changing their category colour. */
const SELECTED_STROKE_WEIGHT_BONUS = 2;

export const SiteLayerOverlays: React.FC<SiteLayerOverlaysProps> = ({
  layers,
  hiddenCategories,
  hiddenLayerIds,
  hiddenGeometryTypes,
  fillOpacity = 0.2,
  strokeOpacity = 1,
  strokeWeight = 2,
  categoryOpacity,
  onLayerClick,
  selectedLayerIds,
}) => {
  const map = useMap();
  const overlaysRef = useRef<Array<google.maps.Polygon | google.maps.Polyline | google.maps.marker.AdvancedMarkerElement>>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const listenersRef = useRef<google.maps.MapsEventListener[]>([]);
  // Held in a ref so a new callback identity each render never tears down and
  // rebuilds every Maps overlay.
  const onLayerClickRef = useRef(onLayerClick);
  onLayerClickRef.current = onLayerClick;

  const visibleLayers = useMemo(
    () => layers.filter(l =>
      l.visible !== false
      && !hiddenCategories?.has(layerCategoryKey(l))
      && !hiddenLayerIds?.has(l.id)
      && !hiddenGeometryTypes?.has(l.geometry.type),
    ),
    [layers, hiddenCategories, hiddenLayerIds, hiddenGeometryTypes],
  );

  useEffect(() => {
    if (!map || typeof google === 'undefined' || !google.maps) return;

    listenersRef.current.forEach(l => l.remove());
    listenersRef.current = [];
    // AdvancedMarkerElement has `.map` (settable HTMLElement-like) instead of
    // `setMap()`; Polygon/Polyline keep `setMap()`. Branch on which API exists.
    overlaysRef.current.forEach(removeOverlayFromMap);
    overlaysRef.current = [];

    if (!infoWindowRef.current) {
      infoWindowRef.current = new google.maps.InfoWindow({
        // A bounded field-note card: wide enough for engineering layer names,
        // still compact on drawers and narrow map surfaces. The supported
        // native header keeps Google's keyboard-accessible close control.
        minWidth: 220,
        maxWidth: 320,
        headerDisabled: false,
      });
    }

    // AdvancedMarkerElement lives in the `marker` library and may not be
    // loaded yet on first render. The marker library is async-imported once
    // and the same effect re-runs synchronously on subsequent renders once
    // it's available. Polygons + Polylines don't depend on it so they render
    // immediately; only the point branch awaits the library handle.
    let cancelled = false;
    void (async () => {
      const markerLib = google.maps.marker ?? (await google.maps.importLibrary('marker') as google.maps.MarkerLibrary);
      if (cancelled) return;

      // Explicit z-index mirrors the array order (last on top) so the
      // persisted layer reorder is deterministic rather than relying on
      // Google's insertion-order stacking.
      // Selection click (studio only): report the layer plus whether a modifier
      // was held, so the caller can extend a multi-selection.
      const attachSelectionClick = (
        overlay: google.maps.Polygon | google.maps.Polyline | google.maps.marker.AdvancedMarkerElement,
        layerId: string,
      ): void => {
        if (!onLayerClickRef.current) return;
        listenersRef.current.push(
          overlay.addListener('click', (event: google.maps.MapMouseEvent) => {
            const dom = event?.domEvent as MouseEvent | undefined;
            const additive = Boolean(dom?.shiftKey || dom?.metaKey || dom?.ctrlKey);
            onLayerClickRef.current?.(layerId, additive);
          }),
        );
      };

      let zIndex = 0;
      for (const layer of visibleLayers) {
        const color = colorForCategory(layer.category, layer.color);
        const tooltipContent = createLayerTooltipContent(layer);
        const layerSelected = selectedLayerIds?.has(layer.id) ?? false;
        const layerStrokeWeight = layerSelected
          ? strokeWeight + SELECTED_STROKE_WEIGHT_BONUS
          : strokeWeight;
        // Noise propagation bands deepen with level: rank 1 (lowest dBA)
        // renders faintest, each band up +0.05 opacity, capped at 0.40 —
        // the classic acoustic-contour gradient.
        const bandRank = typeof layer.attributes?.noiseBandRank === 'number'
          ? layer.attributes.noiseBandRank
          : null;
        const layerFillOpacity = bandRank != null
          ? Math.min(0.4, 0.1 + 0.05 * (bandRank - 1))
          : categoryOpacity?.[layer.category] ?? fillOpacity;
        zIndex += 1;

        if (layer.geometry.type === 'polygon') {
          const poly = new google.maps.Polygon({
            paths: layer.geometry.coordinates,
            strokeColor: color,
            strokeWeight: layerStrokeWeight,
            strokeOpacity,
            fillColor: color,
            fillOpacity: layerFillOpacity,
            map,
            clickable: true,
            zIndex,
          });
          attachInfoWindowClick(poly, map, infoWindowRef.current!, tooltipContent, listenersRef.current);
          attachSelectionClick(poly, layer.id);
          overlaysRef.current.push(poly);
        } else if (layer.geometry.type === 'line') {
          const line = new google.maps.Polyline({
            path: layer.geometry.coordinates,
            strokeColor: color,
            strokeWeight: layerStrokeWeight,
            strokeOpacity,
            map,
            clickable: true,
            zIndex,
          });
          attachInfoWindowClick(line, map, infoWindowRef.current!, tooltipContent, listenersRef.current);
          attachSelectionClick(line, layer.id);
          overlaysRef.current.push(line);
        } else if (layer.geometry.type === 'point' && layer.geometry.coordinates[0]) {
          // Migrated from `new google.maps.Marker({ icon: { path: CIRCLE, ... }})`
          // (deprecated Feb 2024) to AdvancedMarkerElement with a small styled
          // <div> as the `content`. Renders the same colored dot the old icon
          // produced (10px diameter, 1.5px white stroke) without depending on
          // SymbolPath.CIRCLE which AdvancedMarkerElement doesn't support.
          const marker = new markerLib.AdvancedMarkerElement({
            position: layer.geometry.coordinates[0],
            content: createCircleMarkerElement(color),
            map,
            gmpClickable: true,
          });
          attachInfoWindowClick(marker, map, infoWindowRef.current!, tooltipContent, listenersRef.current);
          attachSelectionClick(marker, layer.id);
          overlaysRef.current.push(marker);
        }
      }
    })();

    return () => {
      cancelled = true;
      listenersRef.current.forEach(l => l.remove());
      listenersRef.current = [];
      overlaysRef.current.forEach(removeOverlayFromMap);
      overlaysRef.current = [];
      infoWindowRef.current?.close();
    };
  }, [map, visibleLayers, fillOpacity, strokeOpacity, strokeWeight, categoryOpacity, selectedLayerIds]);

  return null;
};

/**
 * Content for Google's native InfoWindow. The title is assigned to the
 * supported header slot so the native close control shares that row instead
 * of sitting alone above an empty band. Body metadata reads like a compact
 * field note: category, geometry kind, source, then an optional description.
 *
 * DOM nodes + `textContent` are deliberate. Imported layer names and
 * descriptions stay inert without maintaining a parallel HTML-escaping path,
 * while class-based styles remain explicit for Google's always-white bubble.
 */
interface LayerTooltipContent {
  headerContent: HTMLElement;
  bodyContent: HTMLElement;
  ariaLabel: string;
}

const GEOMETRY_LABELS: Record<SiteLayer['geometry']['type'], string> = {
  polygon: 'Area',
  line: 'Line',
  point: 'Point',
};

const SOURCE_LABELS: Record<SiteLayer['source'], string> = {
  'kmz-import': 'KMZ',
  'shapefile-import': 'Shapefile',
  'geojson-import': 'GeoJSON',
  'cad-import': 'DWG',
  manual: 'Manual',
  'arcgis-import': 'ArcGIS',
  'peeringdb-import': 'PeeringDB',
  'landgate-import': 'LandGate',
  'doc-derived': 'Document',
  'scenario-derived': 'Scenario',
};

function createLayerTooltipContent(layer: SiteLayer): LayerTooltipContent {
  const categoryLabel = CATEGORY_LABELS[layer.category];
  const geometryLabel = GEOMETRY_LABELS[layer.geometry.type];
  const sourceLabel = SOURCE_LABELS[layer.source];

  const headerContent = document.createElement('div');
  headerContent.className = 'map-layer-iw__header';
  headerContent.setAttribute('role', 'heading');
  headerContent.setAttribute('aria-level', '2');

  const accent = document.createElement('span');
  accent.className = 'map-layer-iw__accent';
  accent.setAttribute('aria-hidden', 'true');
  // Always use the palette's on-light sibling: the native bubble stays white
  // even when the surrounding app and map are dark.
  accent.style.backgroundColor = colorForCategory(layer.category, layer.color, 'on-light');

  const title = document.createElement('span');
  title.className = 'map-layer-iw__title';
  title.textContent = layer.name;
  headerContent.append(accent, title);

  const bodyContent = document.createElement('div');
  bodyContent.className = 'map-layer-iw';

  const facts = document.createElement('div');
  facts.className = 'map-layer-iw__facts';

  const category = document.createElement('span');
  category.className = 'map-layer-iw__category';
  category.textContent = categoryLabel;

  const metadata = document.createElement('span');
  metadata.className = 'map-layer-iw__meta';
  metadata.textContent = `${geometryLabel} · ${sourceLabel}`;
  facts.append(category, metadata);
  bodyContent.append(facts);

  if (layer.description?.trim()) {
    const description = document.createElement('div');
    description.className = 'map-layer-iw__desc';
    description.textContent = layer.description.trim();
    bodyContent.append(description);
  }

  return {
    headerContent,
    bodyContent,
    ariaLabel: `${layer.name} — ${categoryLabel}, ${geometryLabel}`,
  };
}

function attachInfoWindowClick(
  overlay: google.maps.Polygon | google.maps.Polyline | google.maps.marker.AdvancedMarkerElement,
  map: google.maps.Map,
  iw: google.maps.InfoWindow,
  tooltip: LayerTooltipContent,
  listenerStore: google.maps.MapsEventListener[],
): void {
  // Polygons / Polylines fire click events with `latLng`. AdvancedMarkerElement
  // doesn't — open the InfoWindow anchored to the marker so the arrow points
  // at the marker's position. (Pre-migration this used `Marker.getPosition()`
  // — AdvancedMarkerElement has `.position` instead but the anchor API is
  // cleaner and handles the offset automatically.)
  //
  // Event name diverges by overlay type:
  //   - Polygon / Polyline → 'click' (Maps event, carries latLng)
  //   - AdvancedMarkerElement → 'gmp-click' (Maps deprecated 'click' on
  //     advanced markers in 2024 and now emits a runtime warning when
  //     'click' is used; 'gmp-click' is the supported event and carries
  //     no latLng — anchor the InfoWindow to the marker instead).
  const isMarker = isAdvancedMarker(overlay);
  const eventName = isMarker ? 'gmp-click' : 'click';
  const listener = overlay.addListener(eventName, (e: google.maps.MapMouseEvent) => {
    iw.setHeaderContent(tooltip.headerContent);
    iw.setContent(tooltip.bodyContent);
    iw.setOptions({ ariaLabel: tooltip.ariaLabel });
    if (e?.latLng) {
      iw.setPosition(e.latLng);
      iw.open({ map, shouldFocus: false });
    } else if (isMarker) {
      iw.open({ map, anchor: overlay as google.maps.marker.AdvancedMarkerElement, shouldFocus: false });
    } else {
      iw.open({ map, shouldFocus: false });
    }
  });
  listenerStore.push(listener);
}

/** Type guard for the post-migration overlay union. */
function isAdvancedMarker(
  overlay: google.maps.Polygon | google.maps.Polyline | google.maps.marker.AdvancedMarkerElement,
): overlay is google.maps.marker.AdvancedMarkerElement {
  // AdvancedMarkerElement is an HTMLElement subclass with a `.position` field;
  // Polygon + Polyline expose only `.getPath()` / `.getPaths()`.
  return 'position' in overlay && !('getPath' in overlay) && !('getPaths' in overlay);
}

/**
 * Build the small colored dot that used to come from
 * `icon: { path: SymbolPath.CIRCLE, fillColor, scale: 5, strokeColor: '#fff', strokeWeight: 1.5 }`.
 * AdvancedMarkerElement renders an HTMLElement for its `content`.
 *
 * Visual-clarity tweak: the dot is shrunk 10px → 8px and gets a thin dark
 * outer halo (box-shadow ring outside the white border) so that in a TIGHT
 * cluster adjacent dots read as separate circles instead of merging into one
 * blob. The white border + dark halo keep the dot legible on both bright
 * (satellite) and pale (roadmap) tiles. Colour/category mapping is unchanged.
 */
function createCircleMarkerElement(color: string): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = [
    'width:8px',
    'height:8px',
    'border-radius:50%',
    `background-color:${color}`,
    'border:1.5px solid #ffffff',
    // Thin dark halo for cluster separation + contrast on light tiles.
    'box-shadow:0 0 0 1px rgba(0,0,0,0.35)',
    'box-sizing:border-box',
  ].join(';') + ';';
  return el;
}

/**
 * Remove an overlay from the map. Polygons + Polylines expose `setMap(null)`;
 * AdvancedMarkerElement uses a settable `.map` property instead (its `setMap`
 * was removed in the migration — see the @vis.gl docs).
 */
function removeOverlayFromMap(
  overlay: google.maps.Polygon | google.maps.Polyline | google.maps.marker.AdvancedMarkerElement,
): void {
  if (isAdvancedMarker(overlay)) {
    overlay.map = null;
  } else {
    overlay.setMap(null);
  }
}

/* ── Legend (pure UI) ─────────────────────────────────────────────────── */

export interface SiteLayerLegendProps {
  layers: SiteLayer[];
  hiddenCategories: ReadonlySet<SiteLayerCategory>;
  onToggleCategory: (cat: SiteLayerCategory) => void;
  onReset: () => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

const POSITION_CLASSES: Record<NonNullable<SiteLayerLegendProps['position']>, string> = {
  'top-right':    'top-2 right-2',
  'top-left':     'top-2 left-2',
  'bottom-right': 'bottom-2 right-2',
  'bottom-left':  'bottom-2 left-2',
};

export const SiteLayerLegend: React.FC<SiteLayerLegendProps> = ({
  layers,
  hiddenCategories,
  onToggleCategory,
  onReset,
  position = 'top-right',
}) => {
  const [collapsed, setCollapsed] = useState(false);

  const categoriesPresent = useMemo(() => {
    const counts = new Map<SiteLayerCategory, number>();
    for (const l of layers) counts.set(l.category, (counts.get(l.category) ?? 0) + 1);
    return CATEGORY_ORDER.filter(c => counts.has(c)).map(c => ({ category: c, count: counts.get(c)! }));
  }, [layers]);

  if (categoriesPresent.length === 0) return null;

  return (
    <div
      data-testid="site-layer-legend"
      className={`absolute ${POSITION_CLASSES[position]} z-10 bg-background/95 border border-border-subtle backdrop-blur-sm shadow-md max-w-[200px] font-mono rounded-lg overflow-hidden`}
    >
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-3 py-2 type-section text-text-primary hover:bg-state-hover"
      >
        <span>Layers</span>
        <span className="text-text-muted">{collapsed ? '+' : '−'}</span>
      </button>
      {!collapsed && (
        <div className="border-t border-border-subtle">
          {categoriesPresent.map(({ category, count }) => {
            const hidden = hiddenCategories.has(category);
            return (
              <label
                key={category}
                data-testid={`legend-row-${category}`}
                className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-state-hover"
              >
                <input
                  type="checkbox"
                  checked={!hidden}
                  onChange={() => onToggleCategory(category)}
                  className="w-3 h-3 accent-primary"
                  data-testid={`legend-toggle-${category}`}
                />
                <span
                  className="w-3 h-3 inline-block shrink-0"
                  style={{ backgroundColor: LAYER_COLORS[category] }}
                  aria-hidden
                />
                <span className={`flex-1 type-body uppercase tracking-widest ${hidden ? 'text-text-muted line-through' : 'text-text-primary'}`}>
                  {CATEGORY_LABELS[category]}
                </span>
                <span className="type-meta text-text-muted">{count}</span>
                {hidden ? <EyeOff className="w-3 h-3 text-text-muted" /> : <Eye className="w-3 h-3 text-text-secondary" />}
              </label>
            );
          })}
          {hiddenCategories.size > 0 && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="legend-reset"
              className="w-full h-7 rounded-b-lg type-body uppercase tracking-widest text-text-muted hover:text-text-primary border-t border-border-subtle"
              onClick={onReset}
            >
              <RotateCcw className="w-3 h-3 mr-1.5" /> Reset
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Sidebar variant (MV2) ────────────────────────────────────────────── */

/**
 * MV2 — Layers legend laid out as a static sidebar beside the map
 * instead of a floating overlay. Drops the eye-toggle icons in
 * `surfaceMode === 'static'` (PDF/DOCX) — the toggles are non-
 * functional in a static export so they read as visual noise. Zero-
 * count categories are filtered out the same way `SiteLayerLegend`
 * does it, so the rendered list never carries empty rows.
 *
 * The sidebar component owns no state — the parent passes the layers
 * and the optional hidden-set/toggle handler. In a static export the
 * parent passes neither; in interactive the parent passes both and
 * gets click-to-toggle behaviour parallel to the floating legend.
 */
export interface SiteLayerSidebarProps {
  layers: SiteLayer[];
  /** Set of currently-hidden categories. Empty when no toggling happens. */
  /** Hidden category keys — enum values OR user-defined labels. */
  hiddenCategories?: ReadonlySet<string>;
  /** Toggle handler — required for interactive mode, omitted in static. */
  onToggleCategory?: (cat: SiteLayerCategory) => void;
  /** Drives whether interactive chrome (eye toggles, hover affordances) renders. */
  surfaceMode?: 'interactive' | 'static';
}

export const SiteLayerSidebar: React.FC<SiteLayerSidebarProps> = ({
  layers,
  hiddenCategories,
  onToggleCategory,
  surfaceMode = 'interactive',
}) => {
  const categoriesPresent = useMemo(() => {
    const counts = new Map<SiteLayerCategory, number>();
    for (const l of layers) counts.set(l.category, (counts.get(l.category) ?? 0) + 1);
    return CATEGORY_ORDER.filter(c => counts.has(c)).map(c => ({ category: c, count: counts.get(c)! }));
  }, [layers]);

  if (categoriesPresent.length === 0) return null;

  const isStatic = surfaceMode === 'static';
  const total = layers.length;

  return (
    <aside
      data-testid="site-layer-sidebar"
      data-surface-mode={surfaceMode}
      className="border border-border-subtle bg-background/95 font-mono flex flex-col self-start rounded-lg overflow-hidden"
    >
      <div className="px-3 py-2 type-section text-text-primary border-b border-border-subtle">
        Layers
      </div>
      <div>
        {categoriesPresent.map(({ category, count }) => {
          const hidden = hiddenCategories?.has(category) ?? false;
          const RowTag: 'label' | 'div' = isStatic ? 'div' : 'label';
          return (
            <RowTag
              key={category}
              data-testid={`sidebar-row-${category}`}
              className={`flex items-center gap-2 px-3 py-1.5 ${isStatic ? '' : 'cursor-pointer hover:bg-state-hover'}`}
            >
              {/* Interactive: checkbox + eye icons drive toggling. Static:
                 those affordances are dropped — only the color swatch +
                 label + count remain. */}
              {!isStatic && onToggleCategory && (
                <input
                  type="checkbox"
                  checked={!hidden}
                  onChange={() => onToggleCategory(category)}
                  className="w-3 h-3 accent-primary"
                  data-testid={`sidebar-toggle-${category}`}
                />
              )}
              <span
                className="w-3 h-3 inline-block shrink-0"
                style={{ backgroundColor: LAYER_COLORS[category] }}
                aria-hidden
              />
              <span className={`flex-1 type-body uppercase tracking-widest ${hidden ? 'text-text-muted line-through' : 'text-text-primary'}`}>
                {CATEGORY_LABELS[category]}
              </span>
              <span className="type-meta text-text-muted tabular-nums">{count}</span>
              {!isStatic && (hidden
                ? <EyeOff className="w-3 h-3 text-text-muted" aria-hidden />
                : <Eye className="w-3 h-3 text-text-secondary" aria-hidden />)}
            </RowTag>
          );
        })}
      </div>
      <div className="px-3 py-1.5 type-meta uppercase tracking-widest text-text-muted border-t border-border-subtle">
        {total} layer{total === 1 ? '' : 's'}
      </div>
    </aside>
  );
};

/* ── Per-layer sidebar (item-level toggles for report map) ─────────── */

export interface SiteLayerItemSidebarProps {
  layers: SiteLayer[];
  hiddenLayerIds?: ReadonlySet<string>;
  onToggleLayer?: (id: string) => void;
  surfaceMode?: 'interactive' | 'static';
  /**
   * Phase 4 — swatch/label colour tone. Defaults to `'on-dark'` (the dark
   * satellite-map tuned palette — byte-stable for legacy reports). A
   * professional light figure passes `'on-light'`. Threaded straight into the
   * shared {@link LayerTagChip} (the colour chokepoint).
   */
  tone?: LayerColorTone;
}

export const SiteLayerItemSidebar: React.FC<SiteLayerItemSidebarProps> = ({
  layers,
  hiddenLayerIds,
  onToggleLayer,
  surfaceMode = 'interactive',
  tone = 'on-dark',
}) => {
  if (layers.length === 0) return null;

  const isStatic = surfaceMode === 'static';
  const visibleCount = layers.filter(l =>
    l.visible !== false && !hiddenLayerIds?.has(l.id),
  ).length;

  return (
    <aside
      data-testid="site-layer-item-sidebar"
      data-surface-mode={surfaceMode}
      // STATIC (PDF/print/export): drop the `max-h-[280px]` cap so the panel
      // EXPANDS to show every layer row with NO live scrollbar baked into the
      // static PDF (the "39/39 visible" + scroll-thumb artefact). Interactive
      // keeps the capped, scrollable panel so a long layer list doesn't blow
      // out the on-screen map figure.
      className={`border border-border-subtle bg-background/95 font-mono flex flex-col self-start rounded-lg overflow-hidden${isStatic ? '' : ' max-h-[280px]'}`}
    >
      <div className="px-3 py-2 type-section text-text-primary border-b border-border-subtle shrink-0">
        Layers
      </div>
      {/* STATIC: no `overflow-y-auto` — the rows flow at full height so no
          scroll thumb prints. Interactive keeps the scroll inside the cap. */}
      <div className={isStatic ? '' : 'overflow-y-auto min-h-0 flex-1'}>
        {layers.map(layer => {
          const hidden = hiddenLayerIds?.has(layer.id) ?? false;
          const label = layer.name?.trim() || layer.id;
          const RowTag: 'label' | 'div' = isStatic ? 'div' : 'label';
          return (
            <RowTag
              key={layer.id}
              data-testid={`layer-item-row-${layer.id}`}
              className={`flex items-center gap-2 px-3 py-1.5 ${isStatic ? '' : 'cursor-pointer hover:bg-state-hover'}`}
            >
              {!isStatic && onToggleLayer && (
                <input
                  type="checkbox"
                  checked={!hidden}
                  onChange={() => onToggleLayer(layer.id)}
                  className="w-3 h-3 accent-primary shrink-0"
                  data-testid={`layer-item-toggle-${layer.id}`}
                />
              )}
              {/* Shared chip primitive — switching figure treatments can never
                  drop a tag because every surface renders this same component. */}
              <LayerTagChip
                category={layer.category}
                label={label}
                colorOverride={layer.color}
                tone={tone}
                showCategory
                hidden={hidden}
                className="flex-1 min-w-0"
              />
              {!isStatic && (hidden
                ? <EyeOff className="w-3 h-3 text-text-muted shrink-0" aria-hidden />
                : <Eye className="w-3 h-3 text-text-secondary shrink-0" aria-hidden />)}
            </RowTag>
          );
        })}
      </div>
      <div className="px-3 py-1.5 type-meta uppercase tracking-widest text-text-muted border-t border-border-subtle shrink-0">
        {visibleCount}/{layers.length} visible
      </div>
    </aside>
  );
};

/* ── Boundary polygon (small helper used by report + landing-page maps) ─ */

export interface SiteBoundaryOverlayProps {
  path: { lat: number; lng: number }[];
  strokeColor?: string;
  fillOpacity?: number;
}

export const SiteBoundaryOverlay: React.FC<SiteBoundaryOverlayProps> = ({
  path,
  strokeColor = '#D97706',
  fillOpacity = 0.15,
}) => {
  const map = useMap();
  const polyRef = useRef<google.maps.Polygon | null>(null);

  useEffect(() => {
    if (!map || path.length < 3 || typeof google === 'undefined' || !google.maps) return;
    const poly = new google.maps.Polygon({
      paths: path,
      strokeColor,
      strokeWeight: 2,
      fillColor: strokeColor,
      fillOpacity,
      map,
    });
    polyRef.current = poly;
    return () => {
      poly.setMap(null);
      polyRef.current = null;
    };
  }, [map, path, strokeColor, fillOpacity]);

  return null;
};

/* ── Combined panel (overlays + legend with state) ───────────────────── */

export interface SiteMapLayersProps {
  layers: SiteLayer[];
  /** Render the floating legend. Default true; preview surfaces pass false. */
  showLegend?: boolean;
  /** Polygon fill alpha for overlays. The drawer uses 0.4. */
  fillOpacity?: number;
  strokeOpacity?: number;
  legendPosition?: SiteLayerLegendProps['position'];
}

export const SiteMapLayers: React.FC<SiteMapLayersProps> = ({
  layers,
  showLegend = true,
  fillOpacity,
  strokeOpacity,
  legendPosition = 'top-right',
}) => {
  const [hidden, setHidden] = useState<Set<SiteLayerCategory>>(() => new Set());

  const toggleCategory = (cat: SiteLayerCategory) => {
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const reset = () => setHidden(new Set());

  return (
    <>
      <SiteLayerOverlays
        layers={layers}
        hiddenCategories={hidden}
        fillOpacity={fillOpacity}
        strokeOpacity={strokeOpacity}
      />
      {showLegend && layers.length > 0 && (
        <SiteLayerLegend
          layers={layers}
          hiddenCategories={hidden}
          onToggleCategory={toggleCategory}
          onReset={reset}
          position={legendPosition}
        />
      )}
    </>
  );
};

/* ── Study-area selected blue overlays & alignment/square previews ────── */

export interface StudyAreaSelectedOverlaysProps {
  polygons: readonly ResolvedBoundaryStudyPolygon[];
  /** Optional stroke color override (defaults to each polygon's `#2563eb` blue). */
  strokeColor?: string;
  fillOpacity?: number;
}

/**
 * Renders study-area-selected layers (`selectedForStudyArea === true`) in blue (`#2563eb`)
 * inside the Boundary / Multi-Parcel drawers regardless of `layer.visible`, so users can
 * trace, snap, align, or compare against selected study-area geometry even when general
 * map-layer display is toggled off (#244 #issuecomment-6607632578).
 */
export const StudyAreaSelectedOverlays: React.FC<StudyAreaSelectedOverlaysProps> = ({
  polygons,
  strokeColor,
  fillOpacity,
}) => {
  const map = useMap();
  const overlaysRef = useRef<google.maps.Polygon[]>([]);

  useEffect(() => {
    overlaysRef.current.forEach((p) => p.setMap(null));
    overlaysRef.current = [];
    if (!map || typeof google === 'undefined' || !google.maps) return;

    const created: google.maps.Polygon[] = [];
    for (const poly of polygons) {
      if (!Array.isArray(poly.points) || poly.points.length < 3) continue;
      const color = strokeColor ?? poly.strokeColor ?? '#2563eb';
      const alpha = fillOpacity ?? poly.fillOpacity ?? 0.18;
      const gPoly = new google.maps.Polygon({
        paths: poly.points.map(([lat, lng]) => ({ lat, lng })),
        strokeColor: color,
        strokeOpacity: 0.95,
        strokeWeight: 2.5,
        fillColor: color,
        fillOpacity: alpha,
        clickable: false,
        zIndex: 25,
        map,
      });
      created.push(gPoly);
    }
    overlaysRef.current = created;

    return () => {
      created.forEach((p) => p.setMap(null));
      overlaysRef.current = [];
    };
  }, [map, polygons, strokeColor, fillOpacity]);

  if (polygons.length === 0) return null;
  return (
    <div
      data-testid="study-area-blue-overlays"
      data-polygon-count={polygons.length}
      className="sr-only"
    >
      {polygons.map((p) => (
        <span
          key={p.id}
          data-testid={`study-area-blue-overlay-${p.id}`}
          data-stroke-color={strokeColor ?? p.strokeColor}
        >
          {p.name}
        </span>
      ))}
    </div>
  );
};

export interface BoundaryAlignmentPreviewOverlayProps {
  preview?: SquareCornersPreview | null;
  guides?: readonly SnapGuide[];
}

/**
 * Renders opt-in alignment/snap guide lines and Square/Rectangle preview polygon
 * before the user clicks Apply or Cancel (#244 #issuecomment-6607686195, #issuecomment-6607716290).
 */
export const BoundaryAlignmentPreviewOverlay: React.FC<BoundaryAlignmentPreviewOverlayProps> = ({
  preview,
  guides = [],
}) => {
  const map = useMap();
  const polyRef = useRef<google.maps.Polygon | null>(null);
  const guideRefs = useRef<google.maps.Polyline[]>([]);

  useEffect(() => {
    if (polyRef.current) {
      polyRef.current.setMap(null);
      polyRef.current = null;
    }
    if (map && preview && preview.squaredPoints.length >= 3 && typeof google !== 'undefined' && google.maps) {
      polyRef.current = new google.maps.Polygon({
        paths: preview.squaredPoints.map(([lat, lng]) => ({ lat, lng })),
        strokeColor: '#0ea5e9',
        strokeOpacity: 0.95,
        strokeWeight: 2.5,
        fillColor: '#0ea5e9',
        fillOpacity: 0.14,
        clickable: false,
        zIndex: 60,
        map,
      });
    }
    return () => {
      if (polyRef.current) {
        polyRef.current.setMap(null);
        polyRef.current = null;
      }
    };
  }, [map, preview]);

  useEffect(() => {
    guideRefs.current.forEach((line) => line.setMap(null));
    guideRefs.current = [];
    if (!map || guides.length === 0 || typeof google === 'undefined' || !google.maps) return;

    const created = guides.map(
      (g) =>
        new google.maps.Polyline({
          path: [
            { lat: g.from[0], lng: g.from[1] },
            { lat: g.to[0], lng: g.to[1] },
          ],
          strokeColor: g.kind === 'orthogonal' ? '#a855f7' : '#38bdf8',
          strokeOpacity: 0.9,
          strokeWeight: 2,
          clickable: false,
          zIndex: 65,
          map,
        }),
    );
    guideRefs.current = created;
    return () => {
      created.forEach((line) => line.setMap(null));
      guideRefs.current = [];
    };
  }, [map, guides]);

  if (!preview && guides.length === 0) return null;
  return (
    <div
      data-testid="boundary-alignment-preview-overlay"
      data-has-preview={Boolean(preview)}
      data-guide-count={guides.length}
      className="sr-only"
    />
  );
};
